import React, { FC, Ref, KeyboardEventHandler } from "react";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { LinkButton } from "@app/components/common";
import {
  ButtonSize,
  ButtonVariant,
  LinkButtonType,
} from "@app/components/common/enums";
import { LanguageSwitcher } from "./components/language-switcher/LanguageSwitcher";

type Props = {
  isNavCollapsed: boolean;
  buttonRef: Ref<HTMLDivElement>;
  isMenuOpen: boolean;
  onToggleMenuState: (state: boolean) => void;
  isCalulated?: boolean;
};

export const Controls: FC<Props> = ({
  isNavCollapsed,
  buttonRef,
  isMenuOpen,
  onToggleMenuState,
  isCalulated,
}) => {
  const { t } = useTranslation("mainnav");
  const handleOnKeyDown: KeyboardEventHandler<HTMLButtonElement> = (event) => {
    if (event.key === "Escape") {
      onToggleMenuState(false);
    }
  };

  return (
    <>
      <div
        className={twMerge(
          classNames(
            "relative flex shrink-0 items-center gap-9 py-[0.9375rem] duration-300",
            "lg:py-4",
            {
              "pointer-events-none invisible relative":
                isNavCollapsed || !isCalulated,
              "animate-fade-in": isCalulated,
            },
          ),
        )}
        ref={buttonRef}
      >
        <LanguageSwitcher />
        <div
          className={classNames("duration-300", {
            "pointer-events-none invisible relative":
              isNavCollapsed || !isCalulated,
            "animate-fade-in": isCalulated,
          })}
        >
          <LinkButton
            to=""
            label={t("cta")}
            type={LinkButtonType.DOWNLOAD_APP}
            variant={ButtonVariant.FILL_INFO}
            size={ButtonSize.STANDART}
          />
        </div>
      </div>
      <div
        className={classNames(
          "absolute right-5 top-1/2 w-fit -translate-y-1/2 duration-300",
          "lg:right-7.5",
          {
            invisible: !isNavCollapsed || !isCalulated,
            "animate-fade-in": isCalulated,
          },
        )}
      >
        {
          <button
            className={classNames(
              "flex h-8 w-7 flex-col items-center justify-center p-0",
            )}
            onClick={() => onToggleMenuState(!isMenuOpen)}
            onKeyDown={handleOnKeyDown}
            aria-label="Main menu"
          >
            <div
              className={classNames(
                "w-full duration-500",
                "before:my-1.5 before:block before:h-0.5 before:bg-primary-content before:duration-500",
                "after:my-1.5 after:block after:h-0.5 after:bg-primary-content after:duration-500",
                {
                  "before:translate-y-2 before:rotate-[135deg] after:-translate-y-2 after:-rotate-[135deg]":
                    isMenuOpen,
                },
              )}
            >
              <div
                className={classNames(
                  "my-1.5 block h-0.5 bg-primary-content duration-500",
                  { "scale-0": isMenuOpen },
                )}
              ></div>
            </div>
          </button>
        }
      </div>
    </>
  );
};
